import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  createWsClient,
  type WsClient,
  type WsClientFrame,
} from '@neon-one/auctions-types';

import type { SessionRefreshResponse } from '@neon-one/auctions-types';
import { env } from '@/shell/env';
import { httpClient } from '@/shell/http';
import { adoptJwt, isJwtExpired } from '@/shell/auth/loadAdminUser';
import { useAdminSession } from '@/shell/stores/adminSession';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected';

// The token for each socket attempt. The WS client retries forever (backoff
// capped at 30s) and calls this every time, so handing it an expired session
// JWT turns into a refused handshake every half-minute for as long as the tab
// stays open — the session keep-alive only refreshes after recent user
// activity, so an unattended console (a projected live dashboard) is exactly
// the tab whose token lapses. Refreshing here gives that tab a working token;
// when the session is truly over, the refresh 401s and the HTTP interceptor
// clears it and sends the tab to /login, which ends the retries.
async function jwtForSocket(): Promise<string | null> {
  const session = useAdminSession();
  const current = session.getJwt();
  if (!current || !isJwtExpired(current)) return current;
  try {
    const resp = await httpClient().post<SessionRefreshResponse>('/admin/session/refresh');
    // A logout or account switch while the refresh was in flight wins: adopting
    // now would resurrect the old identity and rewrite its session cookie.
    if (session.getJwt() !== current) return session.getJwt();
    if (resp.data.jwt && adoptJwt(resp.data.jwt)) return resp.data.jwt;
  } catch {
    // A 401 is handled by the interceptor (clear + redirect). Anything else is
    // treated as transient: fall through, and the next attempt tries again.
  }
  return session.getJwt();
}
type FrameHandler = (frame: WsClientFrame) => void;

export const useWs = defineStore('ws', () => {
  const state = ref<ConnectionState>('idle');
  let client: WsClient | null = null;
  const handlers = new Map<FrameHandler, { sub: ReturnType<WsClient['on']>; channels: Set<string> }>();

  const isConnected = computed(() => state.value === 'connected');
  const isDisconnected = computed(() => state.value === 'disconnected');

  function ensureClient(): WsClient {
    if (client) return client;
    client = createWsClient({
      baseUrl: env.wsBaseUrl,
      getJwt: jwtForSocket,
      onStatusChange: (status) => {
        if (status === 'connecting') state.value = 'connecting';
        else if (status === 'open') state.value = 'connected';
        else if (status === 'closed' || status === 'error') state.value = 'disconnected';
      },
    });
    return client;
  }

  async function connect(): Promise<void> {
    const c = ensureClient();
    try {
      await c.connect();
    } catch {
      // onStatusChange has already moved state to 'disconnected' / 'error'.
    }
  }

  function subscribe(channels: string[], handler: FrameHandler): void {
    const c = ensureClient();
    let entry = handlers.get(handler);
    if (!entry) {
      const channelSet = new Set<string>();
      const filtered: FrameHandler = (frame) => {
        const ch = frame.channel;
        if (!ch || channelSet.has(ch)) handler(frame);
      };
      entry = { sub: c.on(filtered), channels: channelSet };
      handlers.set(handler, entry);
    }
    for (const ch of channels) entry.channels.add(ch);
    c.subscribe(channels);
  }

  function unsubscribe(channels: string[], handler: FrameHandler): void {
    const entry = handlers.get(handler);
    if (entry) {
      for (const ch of channels) entry.channels.delete(ch);
      if (entry.channels.size === 0) {
        entry.sub.unsubscribe();
        handlers.delete(handler);
      }
    }
    client?.unsubscribe(channels);
  }

  /**
   * Detach a frame handler and leave every channel subscription alone.
   *
   * Channels are not ref-counted: `unsubscribe` drops them from the socket for
   * all consumers at once. A component that shares a channel with whatever
   * mounted it (a section inside a page, both watching `event:{uuid}`) must
   * therefore release only its own handler on teardown — calling `unsubscribe`
   * there kills a live feed its host still depends on. Owning the channel is
   * the host's job; the guest owns nothing but its handler.
   *
   * There is no "detach and drop only my channels" middle ground: the wire's
   * unsubscribe op requires at least one channel (`client-ops.ts`, `.min(1)`),
   * and any channel named is dropped for everyone.
   */
  function offFrames(handler: FrameHandler): void {
    handlers.get(handler)?.sub.unsubscribe();
    handlers.delete(handler);
  }

  function disconnect(): void {
    client?.close();
    client = null;
    handlers.clear();
    state.value = 'idle';
  }

  async function reconnect(): Promise<void> {
    if (!client) {
      return connect();
    }
    const snapshot = Array.from(handlers.entries()).map(([h, entry]) => ({
      handler: h,
      channels: Array.from(entry.channels),
    }));
    // Detach from the outgoing client before discarding it. close() leaves its
    // handler set intact, so anything still attached keeps dispatching into
    // these stores for as long as that socket outlives the swap.
    for (const [, entry] of handlers) entry.sub.unsubscribe();
    client.close();
    client = null;
    handlers.clear();
    state.value = 'idle';
    await connect();
    for (const { handler, channels } of snapshot) {
      subscribe(channels, handler);
    }
  }

  return {
    state,
    isConnected,
    isDisconnected,
    connect,
    subscribe,
    unsubscribe,
    offFrames,
    disconnect,
    reconnect,
  };
});
